<template>
    <div class="settings-panel">
        <div class="settings-model-select">
            <label for="model-select">Model:</label>
            <select id="model-select" v-model="internalModel">
                <option v-for="m in models" :key="m.model_name" :value="m.model_name">
                    {{ getModelAlias(m.model_name) }}
                </option>
            </select>
        </div>
        <div v-if="isCosyVoiceModel" class="settings-voice-select">
            <label for="user-voice-select">Voice:</label>
            <select id="user-voice-select" v-model="internalVoice">
                <option v-for="v in voiceOptions" :key="v.voice_name" :value="v">
                    {{ v.voice_name }}
                </option>
            </select>
        </div>
        <component :is="currentSettingsComponent" :config="settingsConfig"
            @update:config="value => $emit('update:settingsConfig', value)" />
    </div>
</template>

<script>
import { inject, defineComponent, computed, ref, watch } from 'vue';
import { checkAuthentication } from '@/api/auth';
import { getModelsByUserId } from '@/api/services';
import { getUserVoicesByUserId } from '@/api/services'
import modelDisplayName from '@/data/ModelDisplayNames.js'
import SettingsKokoroV1_0 from '@/components/models/SettingsKokoroV10.vue';
import SettingsCosyVoice2 from '@/components/models/SettingsCosyVoice2.vue';
import SettingsIndexTTS2 from '@/components/models/SettingsIndexTTS2.vue';

export default defineComponent({
    name: 'SettingsPanel',
    emits: ['update:settingsConfig', 'update:model'],
    props: {
        settingsConfig: {
            type: Object,
            required: true
        },
        model: {
            type: String,
            required: true
        },
    },
    setup(props, { emit }) {
        // Merge modelDisplayName (array of objects) into a single mapping object
        const modelDisplayNameMapping = modelDisplayName.reduce((acc, obj) => {
            return { ...acc, ...obj };
        }, {});
        const ttsSharedData = inject('ttsSharedData')
        // Inject pageLines and editorSelectedLineIndex at top-level
        const editorSelectedLineIndex = inject('editorSelectedLineIndex');
        const pageLines = inject('pageLines');
        const models = ref([]);
        const internalModel = computed({
            get: () => props.model,
            set: value => emit('update:model', value)
        });
        const userVoices = ref([]);

        const voiceOptions = computed(() => [
            { voice_name: 'Default' },
            ...userVoices.value
        ]);
        const internalVoice = ref(voiceOptions.value[0]);

        const MODEL_ORDER = ['kokoro', 'indextts2'];
        const MODEL_LABELS = {
            kokoro: 'Basic',
            indextts2: 'Pro',
        };
        const DEBUG_PREFIX = '[TTS Models]';

        const getModelType = (modelName = '') => {
            const name = String(modelName).toLowerCase();
            if (name.includes('kokoro')) return 'kokoro';
            if (name.includes('indextts2')) return 'indextts2';
            return '';
        };

        const visibleModels = (modelList = []) => {
            const modelsByType = new Map();
            modelList.forEach(model => {
                const type = getModelType(model?.model_name);
                if (type && !modelsByType.has(type)) {
                    modelsByType.set(type, model);
                }
            });
            return MODEL_ORDER.map(type => modelsByType.get(type)).filter(Boolean);
        };

        const isCosyVoiceModel = computed(() => internalModel.value.toLowerCase().includes('cosyvoice'));
        const currentSettingsComponent = computed(() => {
            const modelName = internalModel.value.toLowerCase();
            if (modelName.includes('cosyvoice')) return SettingsCosyVoice2;
            if (modelName.includes('indextts2')) return SettingsIndexTTS2;
            return SettingsKokoroV1_0;
        });

        async function loadModels() {
            try {
                const { userId } = checkAuthentication();
                const modelListData = await getModelsByUserId(userId);
                models.value = Array.isArray(modelListData) ? visibleModels(modelListData) : [];
                const debugRows = Array.isArray(modelListData)
                    ? modelListData.map(model => ({
                        model_id: model?.model_id,
                        model_name: model?.model_name,
                        type: getModelType(model?.model_name) || 'hidden',
                    }))
                    : modelListData;
                const visibleLabels = models.value.map(model => getModelAlias(model.model_name));
                window.__ttsModelsDebug = {
                    rawModels: modelListData,
                    rawRows: debugRows,
                    visibleModels: models.value,
                    visibleLabels,
                    selectedModel: internalModel.value,
                };
                console.log(`${DEBUG_PREFIX} raw API rows:`, debugRows);
                if (Array.isArray(debugRows)) {
                    console.table(debugRows);
                }
                console.log(`${DEBUG_PREFIX} visible dropdown labels:`, visibleLabels);
                console.log(`${DEBUG_PREFIX} visible dropdown models:`, models.value);
                console.log(`${DEBUG_PREFIX} debug snapshot:`, window.__ttsModelsDebug);
                if (models.value.length && !models.value.some(model => model.model_name === internalModel.value)) {
                    emit('update:model', models.value[0].model_name);
                }
            } catch (err) {
                console.error('Failed to load models:', err);
            }
        }

        async function loadVoices() {
            try {
                userVoices.value = await getUserVoicesByUserId();
            } catch (err) {
                console.error('Failed to load voices:', err);
            }
        }

        watch(
            () => ttsSharedData.value.voice_name,
            (newVoiceName) => {
                if (!newVoiceName) { return }
                console.log(newVoiceName)
                const match = voiceOptions.value.find(v => v.voice_name === newVoiceName);
                if (match) {
                    internalVoice.value = { ...match }; // avoid shared reference
                } else {
                    internalVoice.value = { voice_name: 'Default' };
                }
                console.log(internalVoice.value)
            },
            { immediate: true }
        );

        watch(internalVoice, (newVoice) => {
            if (!isCosyVoiceModel.value) return;
            ttsSharedData.value.voice_name = newVoice.voice_name;
            ttsSharedData.value.audio_file_id = newVoice.audio_file_id;
            ttsSharedData.value.voice_instruct_text = newVoice.instruct_text || '';

            // Ensure current line's voice_setting is updated to match newVoice
            if (editorSelectedLineIndex && pageLines && pageLines.value[editorSelectedLineIndex.value]) {
                const vs = pageLines.value[editorSelectedLineIndex.value].voice_setting;
                const parsed = typeof vs === 'string' ? JSON.parse(vs) : (vs || {});
                parsed.voice_name = newVoice.voice_name;
                parsed.audio_file_id = newVoice.audio_file_id;
                parsed.voice_instruct_text = newVoice.instruct_text;
                pageLines.value[editorSelectedLineIndex.value].voice_setting = JSON.stringify(parsed);
            }
        });
        // Alias helper function for model names using the mapping
        function getModelAlias(modelName) {
            const type = getModelType(modelName);
            if (type) return MODEL_LABELS[type];
            return modelDisplayNameMapping[modelName] || modelName;
        }
        return {
            models,
            internalModel,
            userVoices,
            internalVoice,
            voiceOptions,
            isCosyVoiceModel,
            currentSettingsComponent,
            loadModels,
            loadVoices,
            getModelAlias,
        }
    },
    async created() {
        await this.loadModels();
        await this.loadVoices();
    },
});
</script>

<style scoped>
.settings-panel {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    min-height: 0;
    min-width: 0;
    overflow: auto;
    height: 100%;
}

.settings-model-select {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 5px;
    margin-left: 5px;
    margin-right: 5px;
}

.settings-model-select label {
    font-weight: 600;
}

.settings-model-select select#model-select {
    padding: 0.4rem 2rem 0.4rem 0.75rem;
    border: 1px solid var(--border-color, #ccc);
    border-radius: 6px;
    font-size: 0.9rem;
    background: #fff;
    cursor: pointer;
    min-width: 0;
    line-height: 1.2;
    box-shadow: none;
    -moz-appearance: none;
    -webkit-appearance: none;
    appearance: none;
    background-image: linear-gradient(45deg, transparent 50%, #666 50%),
        linear-gradient(135deg, #666 50%, transparent 50%);
    background-position: calc(100% - 14px) calc(50% - 3px), calc(100% - 9px) calc(50% - 3px);
    background-size: 5px 5px, 5px 5px;
    background-repeat: no-repeat;
}

.settings-model-select select#model-select:focus {
    outline: 2px solid var(--accent-color, #409eff);
    outline-offset: 1px;
    border-color: var(--accent-color, #409eff);
}

.settings-model-select select#model-select:hover {
    border-color: var(--accent-color, #409eff);
}

.settings-model-select label {
    margin-right: 0.25rem;
    font-size: 0.9rem;
    color: #444;
}

.settings-voice-select {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-left: 5px;
    margin-right: 5px;
}

.settings-voice-select label {
    font-weight: 600;
}

.settings-voice-select select#user-voice-select {
    padding: 0.4rem 2rem 0.4rem 0.75rem;
    border: 1px solid var(--border-color, #ccc);
    border-radius: 6px;
    font-size: 0.9rem;
    background: #fff;
    cursor: pointer;
    min-width: 0;
    line-height: 1.2;
    box-shadow: none;
    -moz-appearance: none;
    -webkit-appearance: none;
    appearance: none;
    background-image: linear-gradient(45deg, transparent 50%, #666 50%),
        linear-gradient(135deg, #666 50%, transparent 50%);
    background-position: calc(100% - 14px) calc(50% - 3px), calc(100% - 9px) calc(50% - 3px);
    background-size: 5px 5px, 5px 5px;
    background-repeat: no-repeat;
}

.settings-voice-select select#user-voice-select:focus {
    outline: 2px solid var(--accent-color, #409eff);
    outline-offset: 1px;
    border-color: var(--accent-color, #409eff);
}

.settings-voice-select select#user-voice-select:hover {
    border-color: var(--accent-color, #409eff);
}

.settings-voice-select label {
    margin-right: 0.25rem;
    font-size: 0.9rem;
    color: #444;
}

.settings-panel>* {
    min-width: 0;
}

.settings-model-select label,
.settings-voice-select label {
    display: inline-block;
    width: 80px;
}

.settings-model-select select#model-select,
.settings-voice-select select#user-voice-select {
    flex: 1;
    min-width: 0;
}
</style>
